import React, { useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { Input } from 'formik-antd';
import { FormikProps } from 'formik';

import FormItem from 'components/formik/FormItem';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  countriesSelector,
  isCitiesLoadingSelector,
  isCountriesLoadingSelector,
  citiesSelector,
  statesSelector,
  isStatesLoadingSelector,
} from 'redux/selectors/regionSelectors';
import { GeneralInfoFormValues } from '../GeneralInfo';

type Props = {
  formik: FormikProps<GeneralInfoFormValues>;
};

export default function CompanyStateForm(props: Props) {
  const { formik } = props;
  const [selectedCountry, setSelectedCountry] = useState(formik.values.country);
  const [selectedState, setSelectedState] = useState(formik.values.state);
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);

  const filteredCities = useMemo(
    () => cities.filter((city) => city.stateId === selectedState),
    [selectedState, cities],
  );

  const filteredStates = useMemo(
    () => states.filter((state) => state.countryId === selectedCountry),
    [selectedCountry, states],
  );

  const handleCountryChange = (
    value: number,
    formik: FormikProps<GeneralInfoFormValues>,
  ) => {
    setSelectedCountry(value);

    formik.setFieldValue('city', '', false);
    formik.setFieldValue('state', undefined, false);
    formik.setFieldValue('internationalCityName', '', false);
    formik.setFieldValue('internationalStateName', '', false);
  };

  const handleStateChange = (
    stateId: number,
    formik: FormikProps<GeneralInfoFormValues>,
  ) => {
    setSelectedState(stateId);

    formik.setFieldValue('city', '', false);
  };

  return (
    <Row gutter={40}>
      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              name="country"
              label="Country *"
              data-id="clientCompanyCountryField"
            >
              <FormikSelectWithLoading
                isLoading={isCountriesLoading}
                name="country"
                showSearch
                placeholder="Select country"
                optionFilterProp="children"
                onChange={(value: number) => handleCountryChange(value, formik)}
                value={selectedCountry}
              >
                {countries.map((country) => (
                  <FormikSelectWithLoading.Option
                    key={`select-country-${country.id}`}
                    value={country.id || -1}
                  >
                    {country.name}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              name="state"
              label="State *"
              data-id="clientCompanyStateField"
            >
              <FormikSelectWithLoading
                isLoading={isStatesLoading}
                name="state"
                showSearch
                placeholder="Select state"
                optionFilterProp="children"
                onChange={(value: number) => handleStateChange(value, formik)}
              >
                {filteredStates.map((state) => (
                  <FormikSelectWithLoading.Option
                    key={`select-state-${state.id}`}
                    value={state.id || -1}
                  >
                    {state.name}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem
              name="city"
              label="City *"
              data-id="clientCompanyCityField"
            >
              <FormikAutoCompleteWithLoading
                isLoading={isCitiesLoading}
                name="city"
                filterOption
                disabled={!formik.values.state}
                placeholder="Enter city"
                showSearch
              >
                {filteredCities.map((city) => (
                  <FormikAutoCompleteWithLoading.Option
                    key={`company-city-${city.id}`}
                    value={city.name || ''}
                  >
                    {city.name}
                  </FormikAutoCompleteWithLoading.Option>
                ))}
              </FormikAutoCompleteWithLoading>
            </FormItem>
          </Col>
        </Row>
      </Col>

      <Col xs={24} md={12}>
        <Row gutter={20}>
          <Col xs={24} md={12}>
            <FormItem
              name="mainAddress"
              label="Main address *"
              data-id="newClientMainAddressField"
            >
              <Input
                name="mainAddress"
                placeholder="Enter head-office address"
                autoComplete="off"
              />
            </FormItem>
          </Col>

          <Col xs={24} md={12}>
            <FormItem label="ZIP code" data-id="newClientZipField" name="zip">
              <Input
                name="zip"
                className="full-width"
                placeholder="Enter ZIP code"
                autoComplete="off"
              />
            </FormItem>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
